// MUST be first: wipes stale persisted state on a new deployment BEFORE the
// store import below triggers redux-persist rehydration.
import "@/lib/deploymentGuard";

import React from "react";
import ReactDOM from "react-dom/client";
import { Toaster } from "react-hot-toast";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { store, persistor } from "@/redux/store";
import { applySavedTheme } from "@/redux/reducers/ThemeReducer";
import App from "@/App.jsx";
import { CenteredLoader } from "@/components/shared/Loader";

import "assets/css/globals.css";
import "@fontsource/dm-sans";

// Development only, and only half the story: this warns about a module code
// with no entry in moduleSurfaces.js. Whether each entry names a file that
// really carries a gate is checked at build time, by
// scripts/check-module-surfaces.mjs, which can read the source.
if (import.meta.env.DEV) {
  import("@/permissions/moduleSurfaces").then(({ warnOnUnmappedModules }) => warnOnUnmappedModules());
}

// Paint the saved theme before React mounts.
//
// This used to force `app-theme` to "light" on every load, which threw away
// the choice `deploymentGuard` above had just gone out of its way to carry
// across a deploy. It also wrote only the attribute and the class, never the
// `--color-*` variables — and no stylesheet gives those a fallback, so the
// first render, PersistGate's loader included, painted with every colour token
// unresolved until App's effect got round to setting them.
//
// `applySavedTheme` reads the same key the store's `initialState` reads, so the
// two agree from the first frame.
applySavedTheme();

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate loading={<CenteredLoader />} persistor={persistor}>
        <Toaster
          position="bottom-right"
          containerStyle={{ bottom: 0 }}
          toastOptions={{
            style: {
              background: "#1e2030",
              color: "#e2e8f0",
              border: "1px solid #334155",
              borderRadius: "12px",
              fontFamily: "DM Sans, sans-serif",
              fontSize: "14px",
              margin: "10px",
            },
            success: { iconTheme: { primary: "#5b6af5", secondary: "#fff" } },
            error: {
              iconTheme: { primary: "#ef4444", secondary: "#fff" },
            },
          }}
        />
        <App />
      </PersistGate>
    </Provider>
  </React.StrictMode>,
);
